import { useEffect, useState } from "react";
import { storefrontApiRequest, STOREFRONT_QUERY, type ShopifyProduct } from "@/lib/shopify";

/**
 * Fetches product image URLs from Shopify to use as thumbnails (e.g. in pack headers).
 * Excludes the donation product. Cached in module memory to avoid refetching.
 */
let cachedThumbnails: string[] | null = null;
let inflight: Promise<string[]> | null = null;

async function fetchThumbnails(): Promise<string[]> {
  if (cachedThumbnails) return cachedThumbnails;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const data = await storefrontApiRequest(STOREFRONT_QUERY, { first: 20 });
      const edges: ShopifyProduct[] = data?.data?.products?.edges ?? [];
      const urls = edges
        .filter((p) => p.node.handle !== "don-solidaire")
        .map((p) => p.node.images.edges[0]?.node.url)
        .filter((u): u is string => Boolean(u));
      cachedThumbnails = urls;
      return urls;
    } catch (e) {
      console.error("Failed to load pack thumbnails", e);
      return [];
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function useProductThumbnails(count: number) {
  const [thumbs, setThumbs] = useState<string[]>(() => cachedThumbnails ?? []);

  useEffect(() => {
    let cancelled = false;
    fetchThumbnails().then((urls) => {
      if (!cancelled) setThumbs(urls);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Repeat / slice to match required count
  if (thumbs.length === 0) return [] as string[];
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(thumbs[i % thumbs.length]);
  }
  return out;
}
